/**
 * Hollow Barrow hero props (+field, pure): the content's barrow hint kinds (packages/content
 * zones/barrowLayout.ts) as compositions of kit models, Venom Altar procs ('proc:*') and the
 * outdoor procs ('out:*': rune pillars, the throne, crypt jambs, plinths, roots, crystals).
 * Local +z = the hint's facing (toward the room). Deterministic per hint.
 */
import type { HeroContext, HeroExpander, Placement } from '../../dressing/dress';
import { yawToward } from '../../dressing/dress';
import type { PlacedHint } from '../../dressing/arch';
import type { RGB } from '../types';
import { VENOM_HEROES } from '../venomAltar/heroes';

const WHITE: RGB = [1, 1, 1];
const BONE: RGB = [0.92, 0.9, 0.82];
const MOSS: RGB = [0.48, 0.6, 0.42];
const COLD: RGB = [0.8, 0.86, 0.84];

type Local = { lx: number; lz: number };
function at(h: PlacedHint, l: Local): { x: number; y: number } {
  const fx = Math.cos(h.yaw);
  const fy = Math.sin(h.yaw);
  return { x: h.pos.x + -fy * l.lx * h.scale + fx * l.lz * h.scale, y: h.pos.y + fx * l.lx * h.scale + fy * l.lz * h.scale };
}
const faceYaw = (h: PlacedHint, extra = 0): number => yawToward(Math.cos(h.yaw), Math.sin(h.yaw)) + extra;

function put(h: PlacedHint, ctx: HeroContext, asset: string, l: Local, o: Partial<Omit<Placement, 'pos'>> & { dy?: number } = {}): void {
  const pos = at(h, l);
  ctx.place({
    asset,
    pos,
    y: (o.y ?? ctx.surface(pos)) + (o.dy ?? 0) * h.scale,
    yaw: o.yaw ?? faceYaw(h),
    scale: (o.scale ?? 1) * h.scale,
    tint: o.tint ?? WHITE,
    layer: o.layer ?? 'hero',
    r: (o.r ?? 0.5) * h.scale,
    h: (o.h ?? 1) * h.scale,
    castShadow: o.castShadow ?? true,
    ...(o.stretch ? { stretch: o.stretch } : {}),
    ...(o.tilt !== undefined ? { tilt: o.tilt } : {}),
  });
}

const bones = (h: PlacedHint, ctx: HeroContext, n: number, radius: number): void => {
  const kinds = ['kh_bone_A', 'kh_bone_B', 'kh_bone_C', 'kh_skull'];
  for (let i = 0; i < n; i++) {
    const a = ctx.rnd() * Math.PI * 2;
    const r = radius * Math.sqrt(ctx.rnd());
    put(h, ctx, kinds[i % kinds.length]!, { lx: Math.cos(a) * r, lz: Math.sin(a) * r * 0.7 }, { scale: 1.1 + ctx.rnd() * 0.3, yaw: ctx.rnd() * 6.28, layer: 'detail', tint: BONE, castShadow: false, r: 0.3, h: 0.3, dy: 0.1 });
  }
};

const candles = (h: PlacedHint, ctx: HeroContext, n: number, radius: number): void => {
  const kinds = ['kd_candle_triple', 'kd_candle_melted', 'kd_candle_lit'];
  for (let i = 0; i < n; i++) {
    const a = ctx.rnd() * Math.PI * 2;
    const r = radius * Math.sqrt(ctx.rnd());
    put(h, ctx, kinds[i % kinds.length]!, { lx: Math.cos(a) * r, lz: Math.sin(a) * r * 0.7 }, { scale: 1.4 + ctx.rnd() * 0.3, yaw: ctx.rnd() * 6.28, layer: 'detail', castShadow: false, r: 0.2, h: 0.9 });
  }
};

export const BARROW_HEROES: Record<string, HeroExpander> = {
  // Reused from the Venom Altar (stone, bone and fire read the same underground).
  sarcophagus: VENOM_HEROES['sarcophagus']!,
  skull_mound: VENOM_HEROES['skull_mound']!,
  brazier: VENOM_HEROES['brazier']!,
  altar_dais: (h, ctx) => {
    put(h, ctx, 'proc:dais', { lx: 0, lz: 0 }, { r: 1.7, h: 2.2, tint: COLD });
    for (const lx of [-2.6, 2.6]) put(h, ctx, 'out:crystals', { lx, lz: 0.6 }, { scale: 0.9, r: 0.6, h: 1.8 });
    put(h, ctx, 'kh_skull_candle', { lx: 0.9, lz: 0.2 }, { scale: 1.1, dy: 1.5, layer: 'detail', castShadow: false, r: 0.4, h: 1.2 });
    candles(h, ctx, 4, 1.8);
  },
  barrow_arch: (h, ctx) => {
    for (const lx of [-3.4, 3.4]) put(h, ctx, 'out:crypt_jamb', { lx, lz: 0 }, { r: 0.7, h: 5.9 });
    put(h, ctx, 'out:roots', { lx: -2.2, lz: -0.4 }, { scale: 1.4, dy: 2.6, layer: 'detail', castShadow: false, r: 0.4, h: 2 });
    put(h, ctx, 'out:roots', { lx: 2.4, lz: -0.3 }, { scale: 1.3, dy: 2.8, layer: 'detail', castShadow: false, r: 0.4, h: 2 });
    put(h, ctx, 'kn_hanging_moss', { lx: 0, lz: -0.3 }, { scale: 4, dy: 3.6, stretch: [1.6, 1.4, 1], tint: MOSS, layer: 'detail', castShadow: false, r: 0.2, h: 1.5 });
  },
  crypt_arch: (h, ctx) => {
    // Rune-lit jambs either side of a corridor mouth (outside the strip).
    const c = ctx.arch.corridors.find((x) => x.id === h.owner);
    const half = (c?.axis.width ?? 7) / 2 + 0.9;
    for (const sgn of [-1, 1]) {
      const p = at({ ...h, scale: 1 }, { lx: half * sgn, lz: 0 });
      ctx.place({ asset: 'out:crypt_jamb', pos: p, y: ctx.surface(p), yaw: faceYaw(h), scale: h.scale, tint: WHITE, layer: 'hero', r: 0.7, h: 5.9, castShadow: true });
    }
  },
  bone_pile: (h, ctx) => {
    put(h, ctx, 'kh_ribcage', { lx: 0, lz: 0 }, { scale: 1.3, tint: BONE, r: 0.7, h: 0.9 });
    put(h, ctx, 'kh_skull', { lx: 0.8, lz: 0.4 }, { scale: 1.2, tint: BONE, r: 0.4, h: 0.9 });
    bones(h, ctx, 7, 1.6);
  },
  candles: (h, ctx) => {
    put(h, ctx, 'kh_candle_triple', { lx: 0, lz: 0 }, { scale: 1.4, r: 0.4, h: 1.2 });
    candles(h, ctx, 5, 1.2);
  },
  urn: (h, ctx) => {
    put(h, ctx, 'proc:urn', { lx: 0, lz: 0 }, { scale: 1.15, tint: COLD, r: 0.5, h: 1.1 });
    put(h, ctx, 'proc:urn', { lx: 0.9, lz: 0.3 }, { scale: 0.85, tint: COLD, r: 0.4, h: 0.9 });
    put(h, ctx, 'proc:urn_tipped', { lx: -0.8, lz: 0.5 }, { scale: 0.85, tint: COLD, layer: 'detail', castShadow: false, r: 0.4, h: 0.7 });
  },
  lantern_plinth: (h, ctx) => {
    put(h, ctx, 'out:plinth', { lx: 0, lz: 0 }, { r: 1.0, h: 1.7 });
    put(h, ctx, 'kh_lantern_standing', { lx: 0, lz: 0 }, { scale: 1.5, dy: 1.7, r: 0.4, h: 2.2 });
    put(h, ctx, 'out:crystals', { lx: 0.9, lz: 0.8 }, { scale: 0.6, r: 0.4, h: 1.2 });
  },
  grave_banner: (h, ctx) => {
    put(h, ctx, 'kd_banner_thin_green', { lx: 0, lz: -0.2 }, { scale: 1.2, dy: 0.6, tint: [0.6, 0.72, 0.68], r: 0.3, h: 3 });
    candles(h, ctx, 2, 0.8);
  },
  root_tangle: (h, ctx) => {
    put(h, ctx, 'out:roots', { lx: 0, lz: 0 }, { scale: 1.8, r: 0.6, h: 3 });
    put(h, ctx, 'out:roots', { lx: 1.1, lz: 0.3 }, { scale: 1.3, yaw: faceYaw(h, 2.1), r: 0.5, h: 2.4 });
    put(h, ctx, 'kn_hanging_moss', { lx: -0.6, lz: 0 }, { scale: 4, dy: 2.4, stretch: [1, 1.6, 1], tint: MOSS, layer: 'detail', castShadow: false, r: 0.2, h: 1.5 });
    put(h, ctx, 'kn_mushroom_tanGroup', { lx: 0.4, lz: 0.8 }, { scale: 3, layer: 'detail', castShadow: false, r: 0.2, h: 0.3 });
  },
  bone_pillar: (h, ctx) => {
    put(h, ctx, 'out:rune_pillar', { lx: 0, lz: 0 }, { scale: 0.85, r: 1.0, h: 6 });
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      put(h, ctx, 'kh_skull', { lx: Math.cos(a) * 0.95, lz: Math.sin(a) * 0.95 }, { scale: 1.1, yaw: faceYaw(h, a), dy: 0.55, tint: BONE, castShadow: false, r: 0.3, h: 0.8 });
    }
    bones(h, ctx, 5, 1.8);
  },
  tomb_pillar: (h, ctx) => {
    put(h, ctx, 'out:rune_pillar', { lx: 0, lz: 0 }, { scale: 1.05, r: 1.1, h: 7 });
    put(h, ctx, 'out:roots', { lx: 0.5, lz: 0.5 }, { scale: 1.2, dy: 3.2, layer: 'detail', castShadow: false, r: 0.4, h: 2 });
  },
  barrow_throne: (h, ctx) => {
    put(h, ctx, 'out:throne', { lx: 0, lz: 0 }, { r: 1.6, h: 4.6 });
    for (const lx of [-3.2, 3.2]) put(h, ctx, 'out:crystals', { lx, lz: -0.4 }, { scale: 1.1, r: 0.6, h: 2 });
    bones(h, ctx, 6, 2.6);
  },
  cobweb: (h, ctx) => {
    put(h, ctx, 'kn_hanging_moss', { lx: 0, lz: 0 }, { scale: 4.5, dy: 2.6, stretch: [1.4, 1.2, 1], tint: [0.82, 0.84, 0.82], layer: 'detail', castShadow: false, r: 0.2, h: 1.5 });
    candles(h, ctx, 2, 0.6);
  },
};
